<template>
  <div class="xhs-api-login">
    <p class="intro-tip">
      无需浏览器：二维码 / 手机验证码 / Cookie 粘贴。
      <strong>笔记发现必须 PC 会话</strong>，发布笔记需 Creator；两者需分别登录，不能混用。
    </p>

    <div class="tab-row">
      <button
        type="button"
        class="tab-btn"
        :class="{ active: accountType === 'pc' }"
        @click="switchAccountType('pc')"
      >
        PC
        <span class="sess-dot" :class="localSessions.pc ? 'ok' : 'miss'" />
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: accountType === 'creator' }"
        @click="switchAccountType('creator')"
      >
        Creator
        <span class="sess-dot" :class="localSessions.creator ? 'ok' : 'miss'" />
      </button>
    </div>

    <div class="tab-row sub">
      <button
        type="button"
        class="tab-btn"
        :class="{ active: method === 'qrcode' }"
        @click="switchMethod('qrcode')"
      >
        二维码
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: method === 'phone' }"
        @click="switchMethod('phone')"
      >
        手机验证码
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: method === 'cookie' }"
        @click="switchMethod('cookie')"
      >
        Cookie
      </button>
    </div>

    <div v-if="accountType === 'pc'" class="sync-row">
      <el-checkbox v-model="syncCreator" :disabled="localSessions.pc && !localSessions.creator">
        同步 Creator 会话（发布笔记需要）
      </el-checkbox>
    </div>

    <div
      v-if="accountType === 'creator' && localSessions.pc && !localSessions.creator"
      class="sync-row"
    >
      <el-button type="success" :loading="syncingFromPc" @click="syncFromPc">
        从已登录 PC 一键同步 Creator
      </el-button>
    </div>

    <div class="content-card">
      <div v-if="isCurrentLoggedIn && !forceRelogin" class="pane logged-pane">
        <p class="logged-title">{{ accountType === 'pc' ? 'PC' : 'Creator' }} 已登录</p>
        <p v-if="sessionProbe === 'checking'" class="logged-hint">正在向小红书校验会话…</p>
        <p v-else-if="sessionProbe === 'ok'" class="logged-hint logged-ok">
          平台侧会话有效{{ sessionProbeName ? `（${sessionProbeName}）` : '' }}。笔记发现需 PC 会话，发布需 Creator。
        </p>
        <p v-else-if="sessionProbe === 'invalid'" class="logged-hint logged-warn">
          本地已保存 Cookie，但平台校验未通过：{{ sessionProbeMessage || '请重新登录' }}
        </p>
        <p v-else class="logged-hint">
          表示本地已保存 Cookie。若笔记发现/发布报错，请点下方重新登录刷新。
        </p>
        <el-button type="primary" plain @click="forceRelogin = true; maybeStartQr()">
          重新登录 {{ accountType === 'pc' ? 'PC' : 'Creator' }}
        </el-button>
      </div>

      <template v-else>
        <div v-if="method === 'qrcode'" class="pane qrcode-pane">
          <div v-if="qrLoading" class="center-msg">正在生成二维码…</div>
          <div v-else-if="qrImage" class="qr-wrap">
            <img :src="qrImage" alt="登录二维码" class="qr-img" />
            <p class="qr-hint">{{ qrStatusText }}</p>
          </div>
          <div v-else class="center-msg">
            <el-button type="primary" :loading="qrLoading" @click="startQr">获取二维码</el-button>
          </div>
        </div>

        <div v-else-if="method === 'phone'" class="pane phone-pane">
          <el-form label-width="72px" @submit.prevent>
            <el-form-item label="手机号">
              <el-input v-model="phone" placeholder="11 位手机号" maxlength="11" />
            </el-form-item>
            <el-form-item label="验证码">
              <div class="phone-code-row">
                <el-input v-model="smsCode" placeholder="短信验证码" maxlength="6" />
                <el-button :disabled="!canSendSms || smsSending" @click="sendSms">
                  {{ smsCountdown > 0 ? `${smsCountdown}s` : '发送验证码' }}
                </el-button>
              </div>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" :loading="phoneConfirming" @click="confirmPhone">登录</el-button>
            </el-form-item>
          </el-form>
        </div>

        <div v-else class="pane cookie-pane">
          <el-input
            v-model="cookieText"
            type="textarea"
            :rows="5"
            placeholder="粘贴 Cookie 字符串，如 a1=...; web_session=..."
          />
          <el-button type="primary" class="import-btn" :loading="importing" @click="importCookie">
            导入 Cookie
          </el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, watch, reactive } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'

const props = defineProps({
  accountId: { type: String, required: true },
  apiBase: { type: String, required: true },
  sessions: { type: Object, default: null },
})

const emit = defineEmits(['success'])

const notifyDuplicateMerge = (data) => {
  if (!data?.duplicate_merged) return false
  ElMessage.warning(
    data.message
      || `该平台账号已存在于 ${data.canonical_account_id || data.account_id}，已用新 Cookie 覆盖原槽位；${data.removed_account_id || props.accountId} 未保留`,
  )
  return true
}

const finishLogin = (data) => {
  forceRelogin.value = false
  const effectiveId = data?.account_id || data?.canonical_account_id || props.accountId
  if (data?.redirected && data?.message) {
    ElMessage.warning(data.message)
  } else if (!notifyDuplicateMerge(data)) {
    /* 正常成功由父组件提示 */
  }
  emit('success', {
    ...(data || {}),
    platform: 'xiaohongshu',
    account_id: effectiveId,
  })
}

const localSessions = reactive({ pc: false, creator: false })
const accountType = ref('pc')
const method = ref('qrcode')
const syncCreator = ref(true)
const forceRelogin = ref(false)
const syncingFromPc = ref(false)

const sessionId = ref('')
const qrImage = ref('')
const qrLoading = ref(false)
const qrStatus = ref('')
let pollTimer = null

const phone = ref('')
const smsCode = ref('')
const smsSending = ref(false)
const smsCountdown = ref(0)
const phoneConfirming = ref(false)
let smsTimer = null

const cookieText = ref('')
const importing = ref(false)
const sessionProbe = ref('')
const sessionProbeMessage = ref('')
const sessionProbeName = ref('')

const isCurrentLoggedIn = computed(() => {
  return accountType.value === 'pc' ? localSessions.pc : localSessions.creator
})

const qrStatusText = computed(() => {
  if (qrStatus.value === 'scanned') return '已扫码，请在手机上确认'
  if (qrStatus.value === 'confirmed') return '登录成功'
  if (qrStatus.value === 'expired') return '二维码已过期，请重新获取'
  if (qrStatus.value === 'failed') return '登录失败，请重试'
  return '请使用小红书 App 扫码'
})

const canSendSms = computed(() => /^1\d{10}$/.test(phone.value.trim()))

const applySessions = (s) => {
  localSessions.pc = !!(s && s.pc)
  localSessions.creator = !!(s && s.creator)
}

const probeSession = async () => {
  if (!props.accountId) return
  sessionProbe.value = 'checking'
  sessionProbeMessage.value = ''
  sessionProbeName.value = ''
  try {
    const res = await axios.post(`${props.apiBase}/cookies/xhs-api/verify-session`, {
      account_id: props.accountId,
      account_type: accountType.value,
    })
    if (res.data?.ok) {
      sessionProbe.value = 'ok'
      sessionProbeName.value = res.data.display_name || ''
    } else {
      sessionProbe.value = 'invalid'
      sessionProbeMessage.value = res.data?.message || '会话无效'
    }
  } catch (e) {
    sessionProbe.value = 'invalid'
    sessionProbeMessage.value = e.response?.data?.detail || e.message || '校验失败'
  }
}

const applyInitialFocus = () => {
  forceRelogin.value = false
  resetSession()
  if (localSessions.pc && !localSessions.creator) {
    accountType.value = 'creator'
    syncCreator.value = false
  } else if (!localSessions.pc && localSessions.creator) {
    accountType.value = 'pc'
    syncCreator.value = false
  } else {
    accountType.value = 'pc'
    syncCreator.value = !localSessions.pc || !localSessions.creator
  }
  maybeStartQr()
  if (localSessions.pc || localSessions.creator) probeSession()
}

const clearPoll = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const resetSession = () => {
  clearPoll()
  sessionId.value = ''
  qrImage.value = ''
  qrStatus.value = ''
  qrLoading.value = false
}

const maybeStartQr = () => {
  if (method.value !== 'qrcode') return
  if (isCurrentLoggedIn.value && !forceRelogin.value) return
  startQr()
}

const switchAccountType = (type) => {
  if (accountType.value === type) return
  accountType.value = type
  forceRelogin.value = false
  resetSession()
  if (type === 'pc' && localSessions.pc && !localSessions.creator) {
    syncCreator.value = true
  }
  maybeStartQr()
  if ((type === 'pc' && localSessions.pc) || (type === 'creator' && localSessions.creator)) {
    probeSession()
  }
}

const switchMethod = (m) => {
  if (method.value === m) return
  method.value = m
  resetSession()
  maybeStartQr()
}

const updateSessionsFromSaved = (saved, type) => {
  if (type === 'pc') localSessions.pc = true
  else localSessions.creator = true
  if (saved?.sub_type === 'pc' || type === 'pc') localSessions.pc = true
  if (saved?.sub_type === 'creator' || type === 'creator' || saved?.has_cookie) {
    localSessions.creator = true
  }
}

const pollQr = () => {
  clearPoll()
  pollTimer = setInterval(async () => {
    if (!sessionId.value) return
    try {
      const res = await axios.get(`${props.apiBase}/cookies/xhs-api/${sessionId.value}`)
      const data = res.data
      qrStatus.value = data.status || ''
      if (data.status === 'confirmed' && data.saved?.has_cookie) {
        clearPoll()
        updateSessionsFromSaved(data.saved, accountType.value)
        if (data.creator_sync?.saved?.has_cookie) localSessions.creator = true
        if (notifyDuplicateMerge(data)) {
          finishLogin(data)
          return
        }
        const verify = data.verify
        if (verify?.ok) {
          sessionProbe.value = 'ok'
          sessionProbeName.value = verify.display_name || ''
          ElMessage.success(`登录成功${verify.display_name ? `：${verify.display_name}` : ''}`)
        } else if (accountType.value === 'pc') {
          await probeSession()
          if (sessionProbe.value === 'ok') {
            ElMessage.success('登录成功')
          } else {
            sessionProbeMessage.value = verify?.message || sessionProbeMessage.value
            ElMessage.error(sessionProbeMessage.value || 'PC 会话未就绪，请重新扫码')
            forceRelogin.value = true
            return
          }
        } else if (verify) {
          sessionProbe.value = 'invalid'
          sessionProbeMessage.value = verify.message || ''
          ElMessage.warning(`Cookie 已保存，但平台校验：${verify.message || '未通过'}`)
        } else {
          ElMessage.success('登录成功')
        }
        finishLogin(data)
      } else if (data.status === 'expired' || data.status === 'failed') {
        clearPoll()
        ElMessage.warning(data.message || '二维码已失效，请重新获取')
        qrImage.value = ''
      }
    } catch (e) {
      const detail = e.response?.data?.detail
      if (detail) {
        clearPoll()
        ElMessage.error(detail)
        forceRelogin.value = true
      }
    }
  }, 2000)
}

const startQr = async () => {
  resetSession()
  qrLoading.value = true
  try {
    const res = await axios.post(`${props.apiBase}/cookies/xhs-api/qrcode`, {
      account_id: props.accountId,
      account_type: accountType.value,
      sync_creator: accountType.value === 'pc' ? syncCreator.value : false,
    })
    sessionId.value = res.data.session_id
    qrImage.value = res.data.qr_image_data_url || ''
    if (!qrImage.value) {
      ElMessage.error('未能生成二维码')
      return
    }
    pollQr()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '获取二维码失败')
  } finally {
    qrLoading.value = false
  }
}

const syncFromPc = async () => {
  syncingFromPc.value = true
  try {
    const res = await axios.post(`${props.apiBase}/cookies/xhs-api/sync-creator-from-pc`, {
      account_id: props.accountId,
    })
    if (res.data.ok) {
      localSessions.creator = true
      ElMessage.success('Creator 已从 PC 同步')
      emit('success')
    } else {
      ElMessage.error('同步失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '同步失败')
  } finally {
    syncingFromPc.value = false
  }
}

const startSmsCountdown = () => {
  smsCountdown.value = 60
  if (smsTimer) clearInterval(smsTimer)
  smsTimer = setInterval(() => {
    smsCountdown.value -= 1
    if (smsCountdown.value <= 0) {
      clearInterval(smsTimer)
      smsTimer = null
    }
  }, 1000)
}

const sendSms = async () => {
  if (!canSendSms.value) return
  smsSending.value = true
  try {
    const res = await axios.post(`${props.apiBase}/cookies/xhs-api/phone/send`, {
      account_id: props.accountId,
      account_type: accountType.value,
      phone: phone.value.trim(),
      sync_creator: accountType.value === 'pc' ? syncCreator.value : false,
    })
    sessionId.value = res.data.session_id || ''
    ElMessage.success('验证码已发送')
    startSmsCountdown()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '发送失败')
  } finally {
    smsSending.value = false
  }
}

const confirmPhone = async () => {
  if (!sessionId.value) {
    ElMessage.warning('请先发送验证码')
    return
  }
  if (!smsCode.value.trim()) {
    ElMessage.warning('请输入验证码')
    return
  }
  phoneConfirming.value = true
  try {
    const res = await axios.post(`${props.apiBase}/cookies/xhs-api/phone/confirm`, {
      session_id: sessionId.value,
      phone: phone.value.trim(),
      code: smsCode.value.trim(),
    })
    const data = res.data
    if (data.saved?.has_cookie) {
      updateSessionsFromSaved(data.saved, accountType.value)
      if (data.creator_sync?.saved?.has_cookie) localSessions.creator = true
      if (notifyDuplicateMerge(data)) {
        finishLogin(data)
        return
      }
      const verify = data.verify
      if (verify?.ok) {
        sessionProbe.value = 'ok'
        sessionProbeName.value = verify.display_name || ''
        ElMessage.success(`登录成功${verify.display_name ? `：${verify.display_name}` : ''}`)
      } else if (accountType.value === 'pc') {
        await probeSession()
        if (sessionProbe.value === 'ok') {
          ElMessage.success('登录成功')
        } else {
          ElMessage.error(sessionProbeMessage.value || 'PC 会话未就绪，请重试')
          return
        }
      } else {
        ElMessage.success('登录成功')
      }
      finishLogin(data)
    } else {
      ElMessage.error(data.message || '登录失败')
    }
  } catch (e) {
    const detail = e.response?.data?.detail || '登录失败'
    const hint = String(detail).includes('验证码')
      ? '。若刚失败过，验证码可能已失效，请重新发送验证码'
      : ''
    ElMessage.error(`${detail}${hint}`)
    if (e.response?.status >= 500) {
      sessionId.value = ''
      smsCode.value = ''
    }
  } finally {
    phoneConfirming.value = false
  }
}

const importCookie = async () => {
  const raw = cookieText.value.trim()
  if (!raw) {
    ElMessage.warning('请粘贴 Cookie')
    return
  }
  importing.value = true
  try {
    const res = await axios.post(`${props.apiBase}/cookies/xhs-api/import-cookie`, {
      account_id: props.accountId,
      account_type: accountType.value,
      cookie_string: raw,
      sync_creator: accountType.value === 'pc' ? syncCreator.value : false,
    })
    if (res.data.saved?.has_cookie) {
      updateSessionsFromSaved(res.data.saved, accountType.value)
      if (notifyDuplicateMerge(res.data)) {
        finishLogin(res.data)
        return
      }
      ElMessage.success('Cookie 已导入')
      finishLogin(res.data)
    } else {
      ElMessage.error(res.data.message || '导入失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '导入失败')
  } finally {
    importing.value = false
  }
}

watch(syncCreator, () => {
  if (accountType.value === 'pc' && method.value === 'qrcode' && sessionId.value) {
    resetSession()
    startQr()
  }
})

watch(
  () => props.sessions,
  (s) => {
    applySessions(s)
    applyInitialFocus()
  },
  { immediate: true, deep: true },
)

watch(
  () => props.accountId,
  () => {
    phone.value = ''
    smsCode.value = ''
    cookieText.value = ''
    applyInitialFocus()
  },
)

onBeforeUnmount(() => {
  clearPoll()
  if (smsTimer) clearInterval(smsTimer)
})
</script>

<style scoped>
.xhs-api-login {
  padding: 4px 0 8px;
}

.intro-tip {
  margin: 0 0 16px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.tab-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.tab-row.sub {
  margin-bottom: 12px;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 18px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--el-text-color-regular);
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.sess-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sess-dot.ok {
  background: #67c23a;
}

.sess-dot.miss {
  background: #c0c4cc;
}

.tab-btn:hover {
  color: var(--el-text-color-primary);
}

.tab-btn.active {
  background: var(--el-fill-color-dark);
  color: var(--el-text-color-primary);
  font-weight: 500;
}

.sync-row {
  margin-bottom: 12px;
}

.content-card {
  background: #1c1c1e;
  border-radius: 12px;
  padding: 24px 20px;
  min-height: 280px;
}

.pane {
  width: 100%;
}

.logged-pane {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 232px;
  gap: 12px;
  text-align: center;
}

.logged-title {
  margin: 0;
  font-size: 16px;
  color: #67c23a;
  font-weight: 500;
}

.logged-hint {
  margin: 0;
  font-size: 13px;
  color: #a1a1a6;
  max-width: 320px;
  line-height: 1.5;
}

.logged-ok { color: #95d475; }
.logged-warn { color: #e6a23c; }

.qrcode-pane {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 232px;
}

.center-msg {
  color: #a1a1a6;
  font-size: 14px;
  text-align: center;
}

.qr-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.qr-img {
  width: 200px;
  height: 200px;
  background: #fff;
  border-radius: 8px;
  padding: 8px;
  object-fit: contain;
}

.qr-hint {
  margin: 0;
  font-size: 13px;
  color: #a1a1a6;
}

.phone-pane :deep(.el-form-item__label) {
  color: #a1a1a6;
}

.phone-pane :deep(.el-input__wrapper) {
  background: #2c2c2e;
  box-shadow: none;
}

.phone-pane :deep(.el-input__inner) {
  color: #f5f5f7;
}

.phone-code-row {
  display: flex;
  gap: 8px;
  width: 100%;
}

.phone-code-row .el-input {
  flex: 1;
}

.cookie-pane :deep(.el-textarea__inner) {
  background: #2c2c2e;
  color: #f5f5f7;
  border: none;
  box-shadow: none;
}

.import-btn {
  margin-top: 12px;
}
</style>
